<script setup lang="ts">
/**
 * SyncQueueIndicator (P.4) — global sync status banner.
 *
 * Renders a sticky banner at the top of any route using AppLayout
 * (Dashboard, ComplaintsList, ComplaintDetail, AdminProducts, etc).
 *
 * Visibility logic:
 *  - Show when `pendingCount > 0` (items waiting to sync)
 *  - Show when `isSyncing === true` (sync in progress)
 *  - Show when `lastSyncSummary` exists AND user hasn't dismissed the
 *    current version (success/error toast)
 *
 * Dismiss behavior:
 *  - Click ✕ → clears `lastSyncSummary` and records `dismissedVersion`
 *  - If new sync happens (version++) → banner reappears naturally
 *  - If pending count > 0 → banner reappears (different condition)
 *
 * Actions:
 *  - "Đồng bộ ngay" button (only when online + has pending): triggerSync()
 *  - "✕" close button: clearResults() + bump dismissedVersion
 *
 * Animations:
 *  - Enter: 200ms slide-down + fade
 *  - Leave: 150ms fade-out (Vue <Transition>)
 */

import { computed, onUnmounted, ref } from "vue";
import { useSyncQueue } from "../composables/useSyncQueue";

const {
  pendingCount,
  isSyncing,
  lastSyncSummary,
  lastSyncVersion,
  triggerSync,
  clearResults,
} = useSyncQueue();

/**
 * Version sentinel: tracks the last version the user dismissed.
 * If `lastSyncVersion` > `dismissedVersion`, banner should show for new updates.
 */
const dismissedVersion = ref<number>(-1);

/**
 * Determine the current state of the banner.
 *  - "pending"  → there are queued items waiting
 *  - "syncing"  → a sync is in progress
 *  - "success"  → last sync completed successfully (show toast briefly)
 *  - "error"    → last sync had failures
 *  - "info"     → last sync was empty / nothing to do
 */
type State = "pending" | "syncing" | "success" | "error" | "info";

const state = computed<State>(() => {
  if (pendingCount.value > 0) return "pending";
  if (isSyncing.value) return "syncing";
  if (lastSyncSummary.value) {
    const s = lastSyncSummary.value.toLowerCase();
    if (s.startsWith("đã đồng bộ") || s.includes("thành công")) return "success";
    if (s.startsWith("lỗi") || s.includes("thất bại")) return "error";
    return "info";
  }
  return "info";
});

const isVisible = computed<boolean>(() => {
  // Always show if pending or syncing
  if (pendingCount.value > 0 || isSyncing.value) return true;
  // Show new sync summary if user hasn't dismissed this version
  if (lastSyncSummary.value && lastSyncVersion.value > dismissedVersion.value) {
    return true;
  }
  return false;
});

const message = computed<string>(() => {
  if (pendingCount.value > 0 && isSyncing.value) {
    return `Đang đồng bộ ${pendingCount.value} khiếu nại...`;
  }
  if (isSyncing.value) {
    return "Đang đồng bộ...";
  }
  if (pendingCount.value > 0) {
    return `${pendingCount.value} khiếu nại đang chờ đồng bộ khi có mạng.`;
  }
  return lastSyncSummary.value ?? "";
});

const icon = computed<string>(() => {
  switch (state.value) {
    case "pending":
      return "📡";
    case "syncing":
      return "🔄";
    case "success":
      return "✓";
    case "error":
      return "⚠️";
    default:
      return "ℹ️";
  }
});

const isOnline = ref<boolean>(
  typeof navigator !== "undefined" ? navigator.onLine : true,
);

function onOnline(): void {
  isOnline.value = true;
}
function onOffline(): void {
  isOnline.value = false;
}

if (typeof window !== "undefined") {
  window.addEventListener("online", onOnline);
  window.addEventListener("offline", onOffline);
  onUnmounted(() => {
    window.removeEventListener("online", onOnline);
    window.removeEventListener("offline", onOffline);
  });
}

async function handleManualSync(): Promise<void> {
  await triggerSync();
}

function handleDismiss(): void {
  // Record the dismissed version so a future sync can re-show.
  dismissedVersion.value = lastSyncVersion.value;
  // If there are pending items we want the banner to STAY until they're
  // synced, so only clear the sync summary, not the queue state.
  if (pendingCount.value === 0 && !isSyncing.value) {
    clearResults();
  }
}
</script>

<template>
  <Transition name="banner">
    <div
      v-if="isVisible"
      class="sync-banner"
      :class="[`state-${state}`, { offline: !isOnline }]"
      role="status"
      aria-live="polite"
      data-testid="sync-queue-indicator"
    >
      <span class="sync-icon" aria-hidden="true">{{ icon }}</span>
      <span class="sync-message" data-testid="sync-queue-message">
        <template v-if="!isOnline && pendingCount > 0">
          Đang offline — {{ message }}
        </template>
        <template v-else>
          {{ message }}
        </template>
      </span>
      <button
        v-if="isOnline && pendingCount > 0 && !isSyncing"
        type="button"
        class="sync-action"
        data-testid="sync-queue-action"
        @click="handleManualSync"
      >
        Đồng bộ ngay
      </button>
      <button
        type="button"
        class="sync-close"
        data-testid="sync-queue-close"
        aria-label="Đóng"
        @click="handleDismiss"
      >
        ✕
      </button>
    </div>
  </Transition>
</template>

<style scoped>
.sync-banner {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.7rem 1rem;
  margin: 0 0 1rem;
  border-radius: 8px;
  font-size: 0.92rem;
  line-height: 1.3;
  border: 1px solid transparent;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.sync-icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.sync-message {
  flex: 1;
  min-width: 0;
  word-wrap: break-word;
}

.sync-action {
  border: 1px solid currentColor;
  background: transparent;
  color: inherit;
  padding: 0.35rem 0.7rem;
  border-radius: 4px;
  font-size: 0.85rem;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
}

.sync-action:hover {
  background: rgba(255, 255, 255, 0.55);
}

.sync-close {
  border: none;
  background: transparent;
  color: inherit;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.2rem 0.4rem;
  line-height: 1;
  opacity: 0.7;
}

.sync-close:hover {
  opacity: 1;
}

/* State colors */
.state-pending {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #78350f;
}

.state-syncing {
  background: #dbeafe;
  border-color: #60a5fa;
  color: #1e40af;
}

.state-success {
  background: #d1fae5;
  border-color: #10b981;
  color: #064e3b;
}

.state-error {
  background: #fee2e2;
  border-color: #ef4444;
  color: #7f1d1d;
}

.state-info {
  background: #f3f4f6;
  border-color: #9ca3af;
  color: #1f2937;
}

.sync-banner.offline {
  opacity: 0.85;
}

/* Animations */
.banner-enter-active,
.banner-leave-active {
  transition:
    transform 200ms ease-out,
    opacity 200ms ease-out;
}

.banner-enter-from {
  opacity: 0;
  transform: translateY(-0.5rem);
}

.banner-leave-to {
  opacity: 0;
  transform: translateY(-0.25rem);
}

/* Mobile: full-width, less padding */
@media (max-width: 720px) {
  .sync-banner {
    margin: 0 -0.5rem 0.75rem;
    border-radius: 0;
    font-size: 0.88rem;
  }
}
</style>
